import { faSquareList } from "@fortawesome/pro-duotone-svg-icons";
import { batch, signal } from "@preact/signals-react";
import { ComboBox, DropDownList } from "@progress/kendo-react-dropdowns";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { Checkbox, Input } from "@progress/kendo-react-inputs";
import { Error } from "@progress/kendo-react-labels";
import { FieldTypes } from "../../Fields/FieldTypes/Fields";
import { ColorChangingIcon } from "../UI/ColorChangingIcon";
import {
  editedPropertyValues,
  galSessionData,
  propertiesVisible,
  selectedFieldIndex,
  selectedFieldName,
  selectedFieldType,
  validateFieldName,
  valueMapData,
} from "./GalleryConstructorSignals";

export const GalleryConstructorGrid = () => {
  return (
    <Grid
      style={{ height: "100%" }}
      data={galSessionData.value}
      dataItemKey="id"
      selectable={{
        enabled: true,
        drag: false,
        cell: false,
        mode: "single",
      }}
    >
      <GridNoRecords>This Gallery has no fields defined.</GridNoRecords>
      <GridColumn
        title=""
        width="50px"
        cells={{
          data: (e) => selectedCell({ dataItem: e.dataItem }),
        }}
      />
      <GridColumn
        field="fieldName"
        title="Field Name"
        cells={{
          data: (e) => cellFieldName({ dataItem: e.dataItem }),
        }}
      />
      <GridColumn
        field="fieldType"
        title="Field Type"
        cells={{
          data: (e) => cellFieldType({ dataItem: e.dataItem }),
        }}
      />
      <GridColumn
        field="valueMap"
        title="Value Mapping"
        cells={{
          data: (e) => cellValueMapping({ dataItem: e.dataItem, valueMapData }),
        }}
      />
      <GridColumn
        cells={{
          data: (e) => cellWithPropertiesButton({ dataItem: e.dataItem }),
        }}
      />
    </Grid>
  );
};

//#region Custom Grid Cells
const gridCellUpdateFocus = (dataRow) => {
  batch(() => {
    selectedFieldName.value = "";
    selectedFieldType.value = "";
  });
  batch(() => {
    const dataRowIndex = galSessionData.value.findIndex((row) => row.id === dataRow.value.id);
    propertiesVisible.value = true;
    selectedFieldName.value = galSessionData.value[dataRowIndex].fieldName;
    selectedFieldType.value = galSessionData.value[dataRowIndex].fieldType;
    selectedFieldIndex.value = dataRowIndex;
  });
};

const cellWithPropertiesButton = ({ dataItem }) => {
  const dataRow = signal({ ...dataItem });

  return (
    <td>
      <ColorChangingIcon
        title="Properties"
        icon={faSquareList}
        initialPrimaryColor="#a6e6ff"
        initialSecondaryColor="#0078d4"
        onClick={() => {
          gridCellUpdateFocus(dataRow);
        }}
      />
    </td>
  );
};

const cellFieldName = ({ dataItem }) => {
  const dataRow = signal({ ...dataItem });

  const handleChange = (e) => {
    dataRow.value = { ...dataRow.value, fieldName: e.target.value };
  };

  const handleBlur = (e) => {
    if (galSessionData.value[selectedFieldIndex.value].fieldName === e.target.value) return;

    const updatedRows = [...galSessionData.value];
    updatedRows[selectedFieldIndex.value] = { ...dataRow.value, fieldName: e.target.value };
    galSessionData.value = updatedRows;
  };

  const validation = validateFieldName(dataRow.value.fieldName || "", dataRow.value.id);

  return (
    <td className="k-vertical-align-top">
      <Input
        defaultValue={dataRow.value.fieldName || ""}
        onChange={(e) => handleChange(e)}
        onBlur={(e) => handleBlur(e)}
        onFocus={() => {
          gridCellUpdateFocus(dataRow);
        }}
        required
        valid={validation.isValid}
      />
      {!validation.isValid && <Error>{validation.errorMessage}</Error>}
    </td>
  );
};

const cellFieldType = ({ dataItem }) => {
  const dataRow = signal({ ...dataItem });

  const handleChange = (e) => {
    dataRow.value = { ...dataRow.value, fieldType: e.target.value };
    const updatedRows = galSessionData.value.map((row) => {
      if (row.id === dataRow.value.id) {
        return {
          ...row,
          fieldType: e.target.value,
        };
      }
      return row;
    });

    galSessionData.value = updatedRows;
    selectedFieldType.value = e.target.value;
  };

  return (
    <td className="k-vertical-align-top">
      <DropDownList
        style={{ padding: 0, verticalAlign: "top" }}
        data={FieldTypes.filter(
          (field) =>
            field !== "Gallery" &&
            field !== "Table" &&
            field !== "FileUpload" &&
            field !== "ContinuousComments" &&
            field !== "TableIndex"
        )}
        defaultValue={dataRow.value.fieldType}
        onChange={(e) => {
          handleChange(e);
        }}
        onFocus={() => {
          gridCellUpdateFocus(dataRow);
        }}
        required
      />
      {!dataRow.value.fieldType && <Error>Field Type is required.</Error>}
    </td>
  );
};

const cellValueMapping = ({ dataItem, valueMapData }) => {
  const dataRow = signal({ ...dataItem });

  const handleChange = (e) => {
    dataRow.value = { ...dataRow.value, valueMap: e.target.value };
    const updatedRows = galSessionData.value.map((row) => {
      if (row.id === dataRow.value.id) {
        return {
          ...row,
          valueMap: e.target.value,
          properties: editedPropertyValues.value[row.fieldName],
        };
      }
      return { ...row, properties: editedPropertyValues.value[row.fieldName] };
    });

    galSessionData.value = updatedRows;
  };

  return (
    <td className="k-vertical-align-top">
      <ComboBox
        data={valueMapData?.value}
        name={"valueMapping" + dataRow.value.id}
        defaultValue={dataRow.value.valueMap}
        onChange={(e) => {
          handleChange(e);
        }}
        onFocus={() => {
          gridCellUpdateFocus(dataRow);
        }}
        clearButton={true}
      />
    </td>
  );
};

const selectedCell = ({ dataItem }) => {
  const dataRow = signal({ ...dataItem });
  const handleChange = () => {
    batch(() => {
      selectedFieldName.value = "";
      selectedFieldType.value = "";
    });
    batch(() => {
      const dataRowIndex = galSessionData.value.findIndex((row) => row.id === dataRow.value.id);
      propertiesVisible.value = true;
      selectedFieldName.value = galSessionData.value[dataRowIndex].fieldName;
      selectedFieldType.value = galSessionData.value[dataRowIndex].fieldType;
      selectedFieldIndex.value = dataRowIndex;
    });
    galSessionData.value = galSessionData.value.map((row) => {
      if (row.id === dataRow.value.id) {
        return {
          ...row,
          properties: editedPropertyValues.value[row.fieldName],
        };
      }
      return { ...row, properties: editedPropertyValues.value[row.fieldName] };
    });
  };

  return (
    <td>
      <Checkbox
        value={
          selectedFieldIndex.value ===
          galSessionData.value.findIndex((row) => row.id === dataRow.value.id)
        }
        onChange={() => {
          handleChange();
        }}
      />
    </td>
  );
};
//#endregion Custom Grid Cells
